import { Subscription } from 'navigator-header';
import { loggerFactory } from 'ert-logger';

const logger = loggerFactory.createLogger('Global Header/publisher.ts');

/**
 * Binds a callback function to a message.
 */
export class SubscriptionImpl implements Subscription  {
    message: string;
    callback: Function;
    once?: boolean;

    /**
     * @param message - The message to which this subscription is bound
     * @param callback - The callback function of the subscription
     * @param once - If true the subscription will get un-subscribed after it was executed once
     */
    constructor(message: string, callback: Function, once?: boolean) {
      this.message = message;
      this.callback = callback;
      this.once = once;
    }

    /**
     * Executes the subscriptions callback function
     * All arguments will be passed to the subscription-callback.
     *
     * @param done - Optional done-callback that will be passed as argument to the subscription callback
     * @param args
     */
    execute(done?: Function, ...args: any[]) {
        // we have try catch here to allow for other subscribers to execute and to log exceptions
        try {
            if (this.once) {
                unsubscribe(this);
            }
            if (done) {
                this.callback(done, ...args);
            } else {
                this.callback(...args);
            }
        } catch (e) {
            logger.error(e);
        }
    }
}

/**
 * Basic implementation of the pup/sub pattern.
 */
const subscriptions: { [key: string]: Subscription[] } = {};

/**
 * Subscribes to a given messsage-id
 *
 * @param message - The message-id this subscription is bound to
 * @param callback - The callback function of the subscription
 * @param once - If true the subscription will be unsubscribed after it was executed once
 * @returns a Subscription object
 */
export function subscribe(message: string, callback: Function, once: boolean = false): Subscription | undefined {
    if (!subscriptions[message]) {
        subscriptions[message] = [];
    }
    const messageSubscriptions = subscriptions[message];
    for (const previous of messageSubscriptions.slice()) {
        // do not subscribe equivalents twice
        if (previous.callback.toString() === callback.toString() && previous.once === once) {
            return previous;
        }
    }
    const subscription = new SubscriptionImpl(message, callback, once);
    messageSubscriptions.push(subscription);
    return subscription;
}
/**
 * Unsubscribes a Subscription
 *
 * @param subscription - The Subscription to un-subscribe
 */
export function unsubscribe(subscription: Subscription): void {
    const messageSubscriptions = subscriptions[subscription.message];
    if (messageSubscriptions) {
        const index = messageSubscriptions.indexOf(subscription);
        if (index > -1) {
            messageSubscriptions.splice(index, 1);
        }
    }
}
/**
 * Returns true if the Publisher has subscribers for a given message-id
 *
 * @param message - The message
 */
export function hasSubscriber(message: string): boolean {
    return !!(subscriptions[message] && subscriptions.hasOwnProperty(message) && subscriptions[message].length);
}

/**
 * Publishes a message and executes all its subscriptions
 *
 * @param message - The message
 * @param args
 */
export function publish(message: string, args: any[] = []): void {
    if (hasSubscriber(message)) {
        const messageSubscriptions = subscriptions[message];
        for (const subscription of messageSubscriptions.slice()) {
            subscription.execute(undefined, ...args);
        }
    }
}

/**
 * Publishes a message and calls each subscription-callback providing a done() function as argument.
 * Returns a Thenable that will only resolve after all subscriptions have called their respective done() functions(
 * or after the expiration of an optional timeout).
 * Use it if subscribers will defer subsequent actions.
 *
 * @param message - The message
 * @param timeout - A timeout after which the publication will be cancelled (resolved as rejected)
 * @param args - Additional arguments that will be passed to the callback function
 * @returns Promise
 */
export function publishAndWait(message: string, timeout?: number, args: any[] = []): Promise<void> {
    return new Promise<void>((resolve, reject) => {
        if (!hasSubscriber(message)) {
            // if there is no subscription resolve immediately
            return resolve();
        }
        // reject after timeout expired (optional)
        let rejectTimerId: any;
        if (timeout) {
            rejectTimerId = setTimeout(() => reject(`"${message}" was not resolved after ${timeout}ms.`), timeout);
        }
        // warn if not resolved after 1s
        const timeoutTimerId = setTimeout(() => logger.warn(`"${message}" was not resolved after 1s.`), 1000);
        // execute subscriptions
        const messageSubscriptions = subscriptions[message];
        const cancelTimers = () => {
            clearTimeout(timeoutTimerId);
            if (rejectTimerId) {
                clearTimeout(rejectTimerId);
            }
        };
        return Promise.all(messageSubscriptions.map(subscription => new Promise(done => subscription.execute(done, ...args))))
            .then(() => {
                cancelTimers();
                resolve();
            })
            .catch(err => {
                cancelTimers();
                reject(err);
            });
    });
}

